/**
 * `.msg` (Outlook) parsing for the whole app, plus the deliverables view model.
 *
 * Two exports with two audiences, and the split is the point:
 *  - {@link readMsgDocument} is the ONE place `.msg` bytes are parsed. Both email surfaces — deal
 *    documents and deal deliverables — read through it, so the library is instantiated and its
 *    fields are extracted exactly once in the codebase.
 *  - {@link parseMsgDeliverable} maps that onto the deliverables render contract.
 *
 * ⚠️ PRESENTATION IS DELIBERATELY *NOT* SHARED. The documents viewer joins recipient NAMES only and
 * has its own card; deliverables format `Name <address>` and reuse the `.eml` card. Those are two
 * shipped designs, and unifying them would silently change what deal documents renders. The parse
 * is what drifts when duplicated; the formatting is a per-surface decision.
 *
 * Adapts a raw `.msg` deliverable into the SAME structure the `.eml` viewer renders.
 *
 * Producing `ParsedEmlDeliverable` rather than a parallel type is the whole design: the viewer's
 * fetch, its two-sided size guard, its loading/error/oversize/empty states and its entire render
 * are then shared, so the two email formats cannot drift apart in how they are presented. Only the
 * parse differs.
 *
 * ⚠️ THE PARSER IS `@kenjiuno/msgreader`, AND THE PREDECESSOR WAS REPLACED FOR CORRECTNESS, NOT
 * FEATURES. `@npeersab/msgreader` was measured against a real 5.5 MB Outlook message and failed
 * three ways, one of them silent data corruption:
 *
 *  - **It corrupted attachments.** Its big-block reader seeks to a stream's first block and reads
 *    `sizeBlock` bytes CONTIGUOUSLY, never walking the FAT chain. A compound file does not store
 *    large streams contiguously, so a 2.8 MB PNG came back the right LENGTH with the wrong BYTES
 *    from offset 32,768 — one FAT boundary in. The user downloaded a half-rendering image, and
 *    nothing anywhere reported an error. Any attachment of 4 KB or more took that path.
 *  - **It NUL-terminated file names**, so `image.png` arrived as ten characters and browsers wrote
 *    the file out as `image.png_`. See {@link cleanFileName}.
 *  - **It could not see `PidTagHtml` at all**, which is where that message kept its only body —
 *    no plain text, no RTF. The preview was simply blank.
 *
 * ⛔ Do not swap back on bundle-size grounds. The replacement costs roughly 178 KB gzipped for
 * `iconv-lite`'s codepage tables, which is why {@link loadMsgReader} loads it dynamically. That
 * weight buys correct bytes.
 *
 * ⛔ THE BODY PUTS THIS MODULE IN THE SAME THREAT MODEL AS `.eml`. This module once stated that the
 * msg path "never produces markup, so it is not an XSS sink at all", and warned against
 * synthesising HTML. Fabricating markup from plain text is still forbidden. Surfacing the message's
 * OWN html is a different act, and the recovered markup is untrusted: it MUST go through
 * `sanitiseBodyHtml` before reaching the renderer's `sandbox=""` iframe, and there is deliberately
 * no path here that reaches `bodyHtml` without it.
 *
 * ⚠️ FAILURE IS NOT AN ERROR. When a message carries no HTML in any property, `bodyHtml` stays null
 * and it renders as plain text exactly as it did before. Keep that fallback: a message this cannot
 * read must never render worse than it used to.
 *
 * ⚠️ STILL A LIMITATION: `cc` is always '' and there is no To/Cc split. The new library DOES expose
 * `recipType`, so this is now a fixable gap rather than a hard one — it is simply out of scope for
 * the body and attachment work.
 */

import type MsgReader from '@kenjiuno/msgreader';
import { resolveFileType } from 'friendly-mimes';

import { sanitiseBodyHtml, type DeliverableAttachment, type ParsedEmlDeliverable } from './emlAdapter';
import { msgRtfToHtml } from './rtfHtml';

/** Sources the adapter accepts. `Blob` is what the viewer hands it after fetching. */
export type MsgSource = ArrayBuffer | Uint8Array | Blob;

/**
 * The library's own parsed shape, derived from its method rather than re-declared: `FieldsData` is
 * not exported from the package, and hand-copying it would be a second source of truth that drifts.
 */
export type MsgFields = ReturnType<MsgReader['getFileData']>;

/** Cached module promise, so concurrent first reads share one fetch rather than racing. */
let readerModule: Promise<typeof MsgReader> | null = null;

/**
 * Loads the parser on first use.
 *
 * ⚠️ DYNAMIC ON PURPOSE — DO NOT CONVERT TO A STATIC IMPORT. `@kenjiuno/msgreader` pulls in
 * `iconv-lite`'s codepage tables, about 178 KB gzipped. This module is imported by both email
 * surfaces, so a static import would put that weight in a chunk every user downloads whether or not
 * they ever open an email. Behind `import()` it becomes its own chunk, fetched only when a `.msg`
 * is actually read.
 * @returns {Promise<typeof MsgReader>} The reader constructor.
 */
function loadMsgReader(): Promise<typeof MsgReader> {
  readerModule ??= import('@kenjiuno/msgreader').then((module) => module.default);
  return readerModule;
}

/**
 * Strips the control characters MAPI string properties carry, which browsers refuse in a file name.
 *
 * ⚠️ NOT DEFENSIVE PADDING — THIS IS A MEASURED BUG. A real Outlook message stored its attachment
 * name as the nine characters of `image.png` followed by a NUL: the MAPI string is NUL-terminated
 * and the terminator was part of the reported value. Chrome substitutes an illegal character in a
 * `download` attribute with an underscore, so the user was handed `image.png_`, which the OS
 * refused to open until they renamed it by hand.
 *
 * Applied at the READ boundary so that no caller can forget it, and kept even where the library
 * happens to trim the terminator itself: the guarantee belongs here, not to a dependency's
 * incidental behaviour.
 *
 * Written as a code-point filter rather than a regular expression deliberately — a character class
 * spelling out a control range invites an editor or tool in the chain to write the literal control
 * bytes into this file, which corrupts the source itself.
 * @param {string} fileName - The attachment name as the library reports it.
 * @returns {string} The name with control characters removed and surrounding space trimmed.
 */
function cleanFileName(fileName: string): string {
  return [...fileName]
    .filter((character) => {
      const code = character.codePointAt(0) ?? 0;
      return code > 0x1f && code !== 0x7f;
    })
    .join('')
    .trim();
}

/**
 * Recovers the message's HTML body, trying each place Outlook is known to keep it.
 *
 * ⚠️ THE ORDER IS EVIDENCE-BASED AND ALL THREE ARMS ARE REACHABLE:
 *  1. `html` (`PidTagHtml`) — raw bytes. A real forwarded Outlook message was found carrying its
 *     body ONLY here, with `body` empty and `compressedRtf` absent entirely. Reading this property
 *     is the single reason the previous library could not render that message at all.
 *  2. `bodyHtml` (`PidTagBodyHtml`) — the same content as a string, which some senders populate
 *     instead.
 *  3. `compressedRtf` — HTML encapsulated inside RTF, which is what Outlook writes for messages
 *     that carry no `PidTagHtml`. Verified against real specimens, so this arm is not speculative.
 *
 * ⛔ The result is UNTRUSTED markup and must be sanitised before it reaches a frame.
 * @param {MsgFields} fields - The parsed message.
 * @returns {string | null} The recovered HTML, or null when the message carries none.
 */
function recoverBodyHtml(fields: MsgFields): string | null {
  if (fields.html != null && fields.html.length > 0) {
    const decoded = new TextDecoder('utf-8').decode(fields.html).trim();
    if (decoded !== '') return decoded;
  }
  const bodyHtml = (fields.bodyHtml ?? '').trim();
  if (bodyHtml !== '') return bodyHtml;
  return msgRtfToHtml(fields.compressedRtf);
}

/** One attachment's name and bytes, as the library returns them. */
export interface MsgAttachment {
  fileName: string;
  content: Uint8Array;
}

/**
 * A read `.msg`: its fields plus the reader's attachment accessor.
 *
 * The accessor is carried rather than the extracted attachments because pulling every attachment's
 * bytes eagerly would defeat the point on a message with large ones — the deliverables viewer needs
 * none of them, and the documents viewer needs them only to build download links.
 */
export interface MsgDocument {
  fields: MsgFields;
  /**
   * Reads one attachment's bytes by index.
   * @param {number} index - Index into `fields.attachments`.
   * @returns {MsgAttachment} The attachment's name and bytes.
   */
  getAttachment(index: number): MsgAttachment;
}

/** A message that yielded nothing renderable. `isEmpty` routes the viewer to its download offer. */
const EMPTY_MESSAGE: ParsedEmlDeliverable = {
  from: '',
  to: '',
  cc: '',
  subject: '',
  date: '',
  bodyHtml: null,
  bodyText: null,
  isEmpty: true,
  forwardedMessageCount: 0,
  attachments: []
};

/** Fallback when an attachment's extension is unknown, absent, or refused by the mime library. */
const FALLBACK_MIME_TYPE = 'application/octet-stream';

/** One attachment plus the content id that decides whether it renders inline or gets listed. */
interface MsgAttachmentEntry extends DeliverableAttachment {
  contentId: string;
}

/**
 * Resolves an attachment's media type from its file name.
 *
 * ⚠️ `resolveFileType` THROWS on an extension it does not know, and is case-sensitive — `.PDF`
 * throws where `.pdf` resolves. Unguarded, one oddly-named attachment would abort the whole parse
 * and blank the viewer. It also answers `application/pgp-encrypted` for an empty string, so the
 * no-extension case is short-circuited before the library is consulted rather than after.
 * @param {string} fileName - The attachment's file name.
 * @returns {string} The resolved media type, or `application/octet-stream`.
 */
function mimeTypeForFileName(fileName: string): string {
  const lastDot = fileName.lastIndexOf('.');
  if (lastDot < 0 || lastDot === fileName.length - 1) return FALLBACK_MIME_TYPE;
  try {
    return resolveFileType(fileName.slice(lastDot).toLowerCase()).mime ?? FALLBACK_MIME_TYPE;
  } catch {
    return FALLBACK_MIME_TYPE;
  }
}

/**
 * Base64-encodes bytes for a `data:` URI, in chunks so a large attachment cannot overflow the
 * argument limit of a single spread call.
 * @param {Uint8Array} bytes - The attachment's contents.
 * @returns {string} The base64 text.
 */
function toBase64(bytes: Uint8Array): string {
  const parts: string[] = [];
  for (let i = 0; i < bytes.length; i += 0x8000) {
    parts.push(String.fromCharCode(...bytes.subarray(i, i + 0x8000)));
  }
  return btoa(parts.join(''));
}

/**
 * Reads every attachment's bytes once, pairing them with the content id used to classify them.
 *
 * Nested messages are excluded here rather than later: their bytes are a whole message the viewer
 * cannot render, and `forwardedMessageCount` already tells the reader they exist.
 * @param {MsgFields['attachments']} attachments - The parsed attachment list, absent when none.
 * @param {MsgDocument['getAttachment']} read - The reader's attachment accessor.
 * @returns {MsgAttachmentEntry[]} One entry per readable, non-message attachment.
 */
function readAttachments(
  attachments: MsgFields['attachments'],
  read: MsgDocument['getAttachment']
): MsgAttachmentEntry[] {
  if (attachments == null) return [];
  const entries: MsgAttachmentEntry[] = [];

  attachments.forEach((attachment, index) => {
    if (attachment.innerMsgContent === true) return;
    try {
      const { fileName, content } = read(index);
      // `fileName` from the accessor is already cleaned; the field copy is not, so clean it too
      // rather than depending on which branch this expression happens to take.
      const name = cleanFileName(attachment.fileName ?? fileName ?? '');
      entries.push({
        fileName: name,
        // `attachMimeTag` is the type the SENDER declared. Preferred over guessing from the
        // extension, which is only a fallback for the attachments that carry no declaration.
        mimeType: attachment.attachMimeTag?.trim() || mimeTypeForFileName(name),
        sizeBytes: content.length,
        content,
        // Outlook writes the id with or without angle brackets depending on version.
        contentId: (attachment.pidContentId ?? '').replace(/^<|>$/g, '').trim()
      });
    } catch {
      // One corrupt attachment stream must not cost the reader the entire message.
    }
  });

  return entries;
}

/**
 * Collects the content ids the body actually references, so an attachment can be classified as
 * inline by EVIDENCE rather than by a flag.
 *
 * `@npeersab/msgreader` exposes no `attachmentHidden`, but the reference itself is the better
 * signal anyway: an image Outlook marked hidden yet never referenced would otherwise vanish from
 * both the body and the list.
 * @param {string | null} html - The recovered HTML body, before sanitisation rewrites `cid:`.
 * @returns {Set<string>} Every content id referenced by the body.
 */
function referencedContentIds(html: string | null): Set<string> {
  if (html === null) return new Set();
  return new Set([...html.matchAll(/cid:([^"'\s>)]+)/gi)].map((match) => match[1].trim()));
}

/**
 * Formats one party as `Name <address>`, falling back to whichever half is present.
 *
 * Deliberately NOT shared with `emlAdapter.formatMailbox`: that one reads `postal-mime`'s
 * `{ name, address }` and this one reads msgreader's `{ name, email }`, so sharing would mean an
 * adapter shim over a six-line function. The OUTPUT shape is what must match, and it does — the
 * renderer's `senderInitials` splits on '<'.
 * @param {string | undefined} name - Display name, when present.
 * @param {string | undefined} email - Address, when present.
 * @returns {string} The display string, or '' when neither part survives trimming.
 */
function formatParty(name: string | undefined, email: string | undefined): string {
  const trimmedName = name?.trim() ?? '';
  const trimmedEmail = email?.trim() ?? '';
  if (trimmedName !== '' && trimmedEmail !== '') return `${trimmedName} <${trimmedEmail}>`;
  return trimmedName !== '' ? trimmedName : trimmedEmail;
}

/**
 * Comma-joins the recipient list, dropping entries that carry neither a name nor an address so no
 * stray separator is rendered.
 * @param {MsgFields['recipients']} recipients - The parsed recipients, absent when there are none.
 * @returns {string} The joined display string, or '' when there are none.
 */
function formatRecipients(recipients: MsgFields['recipients']): string {
  if (recipients == null) return '';
  return recipients
    .map((recipient) => formatParty(recipient.name, recipient.email))
    .filter((formatted) => formatted !== '')
    .join(', ');
}

/**
 * Reads the `Date:` value out of the raw transport headers, verbatim.
 *
 * Verbatim is the evidentiary rule the eml adapter's `resolveDisplayDate` follows and this must
 * match it: a message sent 23:00 -0400 rendered as the next calendar day is a wrong fact. The
 * pattern is anchored per line so `X-Original-Date:` and `Resent-Date:` cannot be mistaken for the
 * real header, and the value is validated as a date so an unparseable one reads as absent rather
 * than as garbage.
 *
 * `headers` is `PidTagTransportMessageHeaders`, which Outlook populates for received mail and
 * frequently omits for sent or drafted mail — an empty date is a normal outcome, not a failure.
 * @param {string | undefined} headers - Raw RFC 5322 transport headers, when the message carries them.
 * @returns {string} The header value, or '' when absent or unparseable.
 */
function resolveDisplayDate(headers: string | undefined): string {
  if (headers == null) return '';
  // `(.*)` can capture a trailing `\r` on CRLF-delimited headers, so the trim is load-bearing.
  const raw = /^date:[ \t]*(.*)$/im.exec(headers)?.[1]?.trim() ?? '';
  if (raw === '') return '';
  return Number.isNaN(new Date(raw).getTime()) ? '' : raw;
}

/**
 * Counts embedded messages — this format's equivalent of the eml adapter's nested `message/rfc822`
 * parts. Both are surfaced as a count rather than dropped silently, because the loss is substance.
 * @param {MsgFields['attachments']} attachments - The parsed attachments, absent when there are none.
 * @returns {number} How many attachments are themselves messages; 0 when none.
 */
function countEmbeddedMessages(attachments: MsgFields['attachments']): number {
  if (attachments == null) return 0;
  return attachments.filter((attachment) => attachment.innerMsgContent === true).length;
}

/**
 * Reads a Blob's bytes via `FileReader`.
 *
 * ⚠️ `FileReader` rather than the shorter `blob.arrayBuffer()` **on purpose**: jsdom's `Blob` does
 * not implement `arrayBuffer`, so the modern call passes in a browser and throws
 * `arrayBuffer is not a function` under the test runner. `FileReader` works in both, so this is one
 * code path rather than a capability check with an untested branch. (`postal-mime` sidesteps the
 * same hole internally, which is why the eml adapter never had to.)
 * @param {Blob} blob - The message bytes.
 * @returns {Promise<ArrayBuffer>} The blob's contents.
 */
function readBlob(blob: Blob): Promise<ArrayBuffer> {
  return new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (): void => {
      // Narrowed rather than cast: `result` is typed `string | ArrayBuffer | null` because the same
      // reader can be asked for text, and only `readAsArrayBuffer` was used here.
      if (reader.result instanceof ArrayBuffer) resolve(reader.result);
      else reject(new Error('FileReader returned no ArrayBuffer'));
    };
    reader.onerror = (): void => reject(reader.error ?? new Error('FileReader failed'));
    reader.readAsArrayBuffer(blob);
  });
}

/**
 * Normalises any accepted source to the `ArrayBuffer` the library's constructor requires.
 *
 * The constructor accepts `ArrayBuffer | DataView` only, and silently misparses anything else
 * rather than rejecting it — so a `Blob` passed straight through would yield a plausible-looking
 * empty message instead of an error.
 * @param {MsgSource} source - The bytes, as a Blob, ArrayBuffer, or view over one.
 * @returns {Promise<ArrayBuffer>} The bytes as a standalone ArrayBuffer.
 */
async function toArrayBuffer(source: MsgSource): Promise<ArrayBuffer> {
  if (source instanceof Blob) return readBlob(source);
  if (source instanceof Uint8Array) {
    // Copied rather than handed over: a view may cover part of a larger buffer, and the library
    // reads the whole buffer it is given.
    return new Uint8Array(source).buffer;
  }
  return source;
}

/**
 * THE SINGLE PLACE `.msg` BYTES ARE PARSED. Both email surfaces read through this.
 *
 * ⚠️ Its reason for existing is DRIFT, not tidiness. Before it, the deal-documents viewer and the
 * deliverables viewer each instantiated `MsgReader` and each mapped `senderName` / `senderEmail` /
 * `recipients` / `body` by hand. Two mappings of one library means a fix to the sender fallback —
 * or a change in the library's API — lands in one and is forgotten in the other, with no test and
 * no type to notice. ⛔ Do not add a third `new MsgReader(...)` anywhere; map from this instead.
 *
 * Returns null rather than throwing, for BOTH failure modes: bytes that are not a readable compound
 * file (the library throws) and a readable file that is not a message (the library reports `error`
 * in-band). Each surface decides what an unreadable message looks like on its own.
 * @param {MsgSource} source - The message bytes.
 * @returns {Promise<MsgDocument | null>} The read message, or null when unreadable.
 * @example
 * const doc = await readMsgDocument(blob);
 * if (doc === null) return; // not a readable message
 * doc.fields.subject;
 */
export async function readMsgDocument(source: MsgSource): Promise<MsgDocument | null> {
  const buffer = await toArrayBuffer(source);

  try {
    const Reader = await loadMsgReader();
    const reader = new Reader(buffer);
    const fields = reader.getFileData();
    // A structurally-readable file that is not a message: the library reports this in-band.
    if (fields.error != null) return null;
    return {
      fields,
      getAttachment: (index: number): MsgAttachment => {
        const { fileName, content } = reader.getAttachment(index);
        // Cleaned HERE so every surface gets a usable name; see `cleanFileName`.
        return { fileName: cleanFileName(fileName), content };
      }
    };
  } catch {
    // The library throws on bytes that are not a readable compound file. Untrusted input, real
    // failure mode — not defensive padding.
    return null;
  }
}

/** A message's renderable body and the attachments worth offering alongside it. */
export interface MsgBody {
  /** Sanitised, cid-resolved HTML, safe to put in a sandboxed frame. Null when there is none. */
  bodyHtml: string | null;
  /** Attachments to offer, excluding anything already rendered inline. */
  attachments: DeliverableAttachment[];
}

/**
 * Turns a read message into a safe body and an attachment list.
 *
 * ⚠️ SHARED BY BOTH EMAIL SURFACES ON PURPOSE, AND THE LINE IS PRECISE: the PARSE and the
 * SANITISATION are shared here; the PRESENTATION is not (see the module header). Deal documents and
 * deal deliverables draw different cards around this, but neither may invent its own way of turning
 * message bytes into markup — that is how one surface ends up with an XSS hole the other fixed.
 *
 * ⛔ This is the ONLY supported way to obtain renderable HTML from a `.msg`. Do not call
 * `recoverBodyHtml` directly from a component: its output is raw untrusted markup.
 * @param {MsgDocument} document - A message from {@link readMsgDocument}.
 * @returns {MsgBody} Sanitised body HTML and the attachments to offer.
 */
export function buildMsgBody(document: MsgDocument): MsgBody {
  const { fields } = document;
  // Untrusted markup, sanitised below and never returned raw.
  const recoveredHtml = recoverBodyHtml(fields);
  const entries = readAttachments(fields.attachments, document.getAttachment);

  // Matched against the RECOVERED html: sanitisation rewrites `cid:` to `data:`, so afterwards
  // there would be nothing left to match on.
  const inlineIds = referencedContentIds(recoveredHtml);
  const cidMap = new Map(
    entries
      .filter((entry) => entry.contentId !== '' && inlineIds.has(entry.contentId))
      .map((entry) => [entry.contentId, `data:${entry.mimeType};base64,${toBase64(entry.content)}`])
  );

  const sanitised = recoveredHtml === null ? null : sanitiseBodyHtml(recoveredHtml, cidMap);
  return {
    // A body that sanitises to structurally-nothing is not worth an empty frame; the plain text is
    // the better record. Mirrors the eml adapter's treatment of a blank HTML part.
    bodyHtml: sanitised === null || sanitised.isBlank ? null : sanitised.html,
    // Anything rendered inline is deliberately absent: listing it again would offer the reader the
    // same image twice, once as a picture and once as a download.
    attachments: entries
      .filter((entry) => entry.contentId === '' || !inlineIds.has(entry.contentId))
      .map(({ fileName, mimeType, sizeBytes, content }) => ({ fileName, mimeType, sizeBytes, content }))
  };
}

/**
 * Parses a `.msg` deliverable into the viewer's render contract.
 *
 * Never rejects. An unreadable file resolves to `isEmpty`, which the viewer renders as "could not
 * be read as an email — download it". That is deliberate and is the truthful outcome: the
 * alternative, a rejected promise, lands on the viewer's error state and offers a **Retry** button
 * for a file that will never parse no matter how many times it is fetched.
 * @param {MsgSource} source - The message bytes.
 * @returns {Promise<ParsedEmlDeliverable>} The adapted message, or an empty one when unreadable.
 * @example
 * const parsed = await parseMsgDeliverable(blob);
 * parsed.bodyHtml; // always null — Outlook bodies arrive as plain text
 */
export async function parseMsgDeliverable(source: MsgSource): Promise<ParsedEmlDeliverable> {
  const document = await readMsgDocument(source);
  if (document === null) return EMPTY_MESSAGE;

  const { fields } = document;
  const from = formatParty(fields.senderName, fields.senderEmail);
  const subject = fields.subject?.trim() ?? '';
  const bodyText = fields.body ?? null;
  const { bodyHtml, attachments } = buildMsgBody(document);

  return {
    from,
    to: formatRecipients(fields.recipients),
    // Always '' — see the module header. The library exposes no recipient type to split on.
    cc: '',
    subject,
    date: resolveDisplayDate(fields.headers),
    bodyHtml,
    bodyText,
    // Mirrors the eml adapter's own emptiness test, which reads body/from/subject and NOT `to`:
    // a recipient list alone would render a card with a Subject heading over nothing.
    isEmpty: (bodyText ?? '').trim() === '' && from.trim() === '' && subject === '' && bodyHtml === null,
    forwardedMessageCount: countEmbeddedMessages(fields.attachments),
    attachments
  };
}
